import React, { useState, useRef } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import classNames from 'classnames';
import styles from '../styles.module.css';
import strokeStyleSVG from '../assets/icons/strokeStyle.svg';
import strokeWidthSVG from '../assets/icons/strokeWidth.svg';
import lineSolidSVG from '../assets/icons/lineSolid.svg';
import lineDashedSVG from '../assets/icons/lineDashed.svg';
import lineDottedSVG from '../assets/icons/lineDotted.svg';
import { svgToIcon } from '../utils/svg';
import ToolbarArea from '../toolbars/ToolbarArea';
import ToolbarRow from '../toolbars/ToolbarRow';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import WithHint from '../toolbars/WithHint';
import { isConnector, isGroupLike } from '../objects/guards';
import { STROKE_TYPES } from '../constants';

type ValueOf<T> = T[keyof T];

export type StrokeType = ValueOf<typeof STROKE_TYPES>;

export function iconForLineType(lineType: ValueOf<typeof STROKE_TYPES>) {
  switch (lineType) {
    case STROKE_TYPES.SOLID:
      return svgToIcon(lineSolidSVG);
    case STROKE_TYPES.DASHED:
      return svgToIcon(lineDashedSVG);
    case STROKE_TYPES.DOTTED:
      return svgToIcon(lineDottedSVG);
    default:
      throw new Error(`Unknown line type ${lineType}`);
  }
}

interface PopupProps {
  showStrokeStyle: boolean;
  showStrokeWidth: boolean;
  currentLineType: number;
  setCurrentLineType: (lineType: number) => void;
  selection: fabric.Object;
  hidePopup: () => void;
}
const Popup: React.FC<PopupProps> = ApplicationView(
  ({
    showStrokeStyle,
    showStrokeWidth,
    currentLineType,
    setCurrentLineType,
    selection,
    hidePopup,
  }) => {
    const controller = useController(WhiteboardController);

    const setLineType = (lineType: number) => {
      setCurrentLineType(lineType);
      selection.setOnObjects('strokeType', lineType);
      Tool.toolActionComplete({ controller });
    };

    const handleChange = event => {
      const applyStrokeWidth = (object: fabric.Object) => {
        if (isGroupLike(object)) {
          object.forEach(applyStrokeWidth);
          object.syncFromObjects();
        } else {
          if (
            !isConnector(object) &&
            object.stroke &&
            object.stroke !== 'transparent'
          ) {
            const diff = object.strokeWidth - event.target?.selectedTickValue;
            object.setOnObjects({
              top: object.top + diff / 2,
              left: object.left + diff / 2,
            });
          }
          object.setOnObjects({
            strokeWidth: event.target?.selectedTickValue,
          });
        }
      };
      selection.forEach(applyStrokeWidth);
      if (isGroupLike(selection)) {
        selection.syncFromObjects();
      }
      controller.canvas.requestRenderAll();
    };

    return (
      <ToolbarMenu columns={3}>
        {showStrokeStyle && (
          <ToolbarRow>
            {Object.entries(STROKE_TYPES).map(([key, lineType]) => (
              <div
                key={key}
                className={classNames(styles.toolButton, {
                  [styles.toolButtonActive]: lineType === currentLineType,
                })}
                onClick={() => {
                  setLineType(lineType);
                  hidePopup();
                }}
                data-testid={`Tool-line_type-${key.toLowerCase()}`}
              >
                {iconForLineType(lineType)}
              </div>
            ))}
          </ToolbarRow>
        )}
        {showStrokeWidth && (
          <ToolbarArea>
            <WithHint name='Stroke width'>{svgToIcon(strokeWidthSVG)}</WithHint>
            <aha-slider
              ticks='[1,2,3,5,10,20,30]'
              start-value={Math.round(
                selection.firstObject().get('strokeWidth')
              )}
              onInput={item => handleChange(item)}
              onMouseUp={() => Tool.toolActionComplete({ controller })}
              data-testid={`Tool-line_width`}
            />
          </ToolbarArea>
        )}
      </ToolbarMenu>
    );
  }
);

class ToolStrokeStyle extends Tool {
  static toolName = 'Line type';

  static showStrokeStyle = true;
  static showStrokeWidth = true;

  static iconSVG = strokeStyleSVG;

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(
      ({ selection, tool }) => {
        const tippyRef = useRef<TippyInstance>();
        const [currentLineType, setCurrentLineType] = useState(
          selection.firstObject().get('strokeType')
        );

        return (
          <ToolButton
            popup={
              <Popup
                showStrokeStyle={this.showStrokeStyle}
                showStrokeWidth={this.showStrokeWidth}
                currentLineType={currentLineType}
                setCurrentLineType={setCurrentLineType}
                selection={selection}
                hidePopup={() => tippyRef.current?.hide()}
              />
            }
            tippyRef={tippyRef}
            id='strokeType'
            tool={tool}
          >
            {svgToIcon(this.iconSVG)}
          </ToolButton>
        );
      }
    );

    return Button;
  }
}

/**
 * Limited version with only stroke width
 */
export class ToolStrokeWidth extends ToolStrokeStyle {
  static iconSVG = strokeWidthSVG;

  static showStrokeStyle = false;
}

export default ToolStrokeStyle;
